import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { notificationService } from '@/services/notificationService';

export function useNotifications({ page = 1, perPage = 10, unreadOnly = false, search = '' } = {}) {
  return useQuery({
    queryKey: ['notifications', { page, perPage, unreadOnly, search }],
    queryFn: async () => {
      const result = await notificationService.getNotifications({
        page,
        per_page: perPage,
        unread_only: unreadOnly,
        search: search || undefined,
      });
      if (!result.success) throw new Error(result.error);
      return { data: result.data, pagination: result.pagination };
    },
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const result = await notificationService.getUnreadCount();
      if (!result.success) throw new Error(result.error);
      return result.data;
    },
    staleTime: 30 * 1000,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const result = await notificationService.markAsRead(id);
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await notificationService.markAllAsRead();
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeleteAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await notificationService.deleteAll();
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
